import React, { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import { BaseButton, BaseIconButton, Tooltip, Typography } from '@lyearn/ui';
import SelectionMenu from '@/modules/tasks/components/SelectionMenu';
import {
  baseButtonFocusStyles,
  TASK_PILL_VARIANT_CONFIG,
  TaskPillVariant,
} from '@/modules/tasks/components/TaskStatus/styles';
import { ActionItemPriorityEnum } from '@/types/schema';

import { ACTION_ITEM_PRIORITY_CONFIG, getActionItemPriorityMenuItems } from './utils';

interface ActionPrioritySelectorProps {
  variant: TaskPillVariant;
  className?: string;
  priority?: ActionItemPriorityEnum;
  editable?: boolean;
  showLabel?: boolean;
  onChange?: (priority: ActionItemPriorityEnum) => void;
  disableInteractiveTooltip?: boolean;
}

const ActionPrioritySelector = ({
  variant,
  className,
  priority = ActionItemPriorityEnum.NoPriority,
  editable = false,
  showLabel = true,
  onChange,
  disableInteractiveTooltip = false,
}: ActionPrioritySelectorProps) => {
  const { t } = useTranslation();

  const config = ACTION_ITEM_PRIORITY_CONFIG[priority];
  const { Icon, labelKey } = config;

  const items = getActionItemPriorityMenuItems(t);
  const variantConfig = TASK_PILL_VARIANT_CONFIG[variant];

  const overrides = useMemo(
    () => ({
      PopoverButton: { className: cn(variantConfig.rootEditable.className, 'cursor-pointer') },
      PopoverContent: { position: variantConfig.popoverPosition },
    }),
    [variantConfig],
  );

  const content = showLabel ? (
    <BaseButton
      aria-label="priority"
      className={cn(
        'w-max p-0 space-s-8 min-w-0',
        baseButtonFocusStyles,
        variantConfig.root.className,
        className,
      )}
      disabled={!editable}>
      <Icon height={16} width={16} />
      <Typography
        className={variantConfig.label.className}
        color={variantConfig.label.color}
        variant="body-short-03">
        {t(labelKey)}
      </Typography>
    </BaseButton>
  ) : (
    <Stack className="border border-transparent rounded-8 hover:border-border hover:bg-transparent active:border-primary py-10 w-full flex">
      <BaseIconButton
        aria-label="priority"
        className={cn(
          'p-0 space-s-8 hover:bg-transparent h-full w-full flex justify-start px-0',
          baseButtonFocusStyles,
          variantConfig.root.className,
          className,
        )}
        disabled={!editable}>
        <Tooltip disableInteractive={disableInteractiveTooltip} title={t(labelKey)}>
          <span>
            <Icon className="h-18 w-18" />
          </span>
        </Tooltip>
      </BaseIconButton>
    </Stack>
  );

  if (editable) {
    return (
      <SelectionMenu items={items} overrides={overrides} onAction={onChange}>
        {content}
      </SelectionMenu>
    );
  }

  return content;
};

export default React.memo(ActionPrioritySelector);
